import { Message } from "domain/entities/message";
import { Button } from "primereact/button";
import { useMemo } from "react";
import MobileBottomSheet from "presentation/mobile/components/MobileBottomSheet";

interface MileageLog {
    month: number;
    year: number;
    kms: number;
    createdAt: string | Date;
}

interface KMHistoryProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    mileageLogsData: Message<MileageLog[]> | undefined;
    isLoading: boolean;
}

const KMHistory = ({
    visible,
    setVisible,
    mileageLogsData,
    isLoading,
}: KMHistoryProps) => {
    const mileageLogs = useMemo(
        () =>
            [...(mileageLogsData?.data ?? [])].sort((firstLog, secondLog) => {
                if (firstLog.year !== secondLog.year) {
                    return secondLog.year - firstLog.year;
                }

                return secondLog.month - firstLog.month;
            }),
        [mileageLogsData?.data],
    );

    const latestLog = mileageLogs[0];

    const formatPeriod = (log: MileageLog) =>
        `${log.month.toString().padStart(2, "0")}/${log.year}`;

    const formatKms = (kms: number) =>
        `${kms.toLocaleString("pt-PT")} km`;

    const formatDate = (date: string | Date) =>
        new Date(date).toLocaleDateString("pt-PT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });

    return (
        <MobileBottomSheet
            visible={visible}
            onHide={() => setVisible(false)}
            title="Histórico de KMs"
            headerIcon="pi pi-history"
        >
            <div className="flex flex-column gap-3 py-2">
                {/* Card de Resumo do Último Registo */}
                <div
                    className="border-round-xl p-3 flex justify-content-between align-items-center"
                    style={{ backgroundColor: "#F0F9FF", border: "1px solid #BAE6FD" }}
                >
                    <div className="flex flex-column gap-1">
                        <span className="text-xs text-600 font-medium">Último registo</span>
                        <span className="text-xl font-bold" style={{ color: "#0084D1" }}>
                            {latestLog ? formatKms(latestLog.kms) : "-"}
                        </span>
                    </div>
                    <i className="pi pi-chart-line text-2xl" style={{ color: "#0084D1" }} />
                </div>

                {isLoading ? (
                    <div className="flex flex-column gap-2">
                        {[1, 2, 3].map((item) => (
                            <div key={item} className="card-mobile p-3">
                                <div className="h-1rem w-5 bg-gray-200 border-round mb-2" />
                                <div className="h-1rem w-7 bg-gray-200 border-round" />
                            </div>
                        ))}
                    </div>
                ) : mileageLogs.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center gap-2 py-6">
                        <i className="pi pi-history text-4xl text-400" />
                        <span className="text-sm text-500 text-center">
                            Sem registos de KMs para esta viatura.
                        </span>
                    </div>
                ) : (
                    <div className="flex flex-column gap-2">
                        <span className="text-xs font-semibold text-500">
                            {mileageLogs.length} {mileageLogs.length === 1 ? "registo encontrado" : "registos encontrados"}
                        </span>

                        {mileageLogs.map((log) => (
                            <div
                                key={`${log.year}-${log.month}`}
                                className="card-mobile p-3 flex flex-column gap-2"
                            >
                                <div className="flex justify-content-between align-items-start">
                                    <div className="flex flex-column">
                                        <span className="text-xs text-500">Período</span>
                                        <span className="text-base font-bold text-800">
                                            {formatPeriod(log)}
                                        </span>
                                    </div>
                                    <span
                                        className="text-base font-bold"
                                        style={{ color: "#0084D1" }}
                                    >
                                        {formatKms(log.kms)}
                                    </span>
                                </div>

                                <div
                                    className="flex justify-content-between align-items-center pt-2"
                                    style={{ borderTop: "1px solid #F1F5F9" }}
                                >
                                    <span className="text-xs text-400">
                                        Registado em {formatDate(log.createdAt)}
                                    </span>

                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </MobileBottomSheet>
    );
};

export default KMHistory;